import { groupBy, map, orderBy, size } from "lodash"; // cloneDeep removed
import { useMemo } from "react";
import { shallowEqual } from "react-redux";
import { OperationMode } from "../components/Layout/Offsidebar";
import { DisplayType, OrderModeType, ProgressType } from "../components/Layout/OmsSelector";
import { isDocketActive, isDocketCompleted } from "../helpers/dockets";
import { DocketFromDocket, OMSDisplayDocket, OMSDocketGeneratedFrom } from "../resbutler-utils/types/Docket";
import { DocketType, OrderItemStatus } from "../resbutler-utils/types/Order";
import { useAppSelector } from "./hook";

type ProgressCounts = {
  allCount: number;
  activeCount: number;
  completedCount: number;
};

type PermutationKey = `${OperationMode}:${DisplayType}`;

const toPermutationKey = (operationMode: OperationMode, display: DisplayType): PermutationKey => `${operationMode}:${display}`;

const docketHasBooking = (docket: OMSDisplayDocket): boolean => !!(docket.bookingId || docket.functionBookingId);

const splitByBooking = (dockets: (OMSDisplayDocket & DocketFromDocket)[]) => ({
  withBooking: dockets.filter(docketHasBooking),
  withoutBooking: dockets.filter((d) => !docketHasBooking(d)),
});

const groupDocketsByBookingId = (dockets: (OMSDisplayDocket & DocketFromDocket)[]): Map<string, (OMSDisplayDocket & DocketFromDocket)[]> => {
  const map = new Map<string, (OMSDisplayDocket & DocketFromDocket)[]>();

  for (const docket of dockets) {
    const key = docket.bookingId || docket.functionBookingId;
    if (key) {
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(docket);
    }
  }

  return map;
};

const isDocketFinishedBeforeNext = (docket: OMSDisplayDocket, nextDocket: OMSDisplayDocket, operationMode: OperationMode, display: DisplayType): boolean => {
  const items = Object.values(docket.docketItems);
  if (items.length === 0) return false;

  if (operationMode === OperationMode.OneStep) {
    return items.every((item) => item.orderStatus === OrderItemStatus.Served && item.completedTime < (nextDocket as DocketFromDocket).docketGenerationTime);
  }

  if (display === DisplayType.PrepDisplay) {
    return items.every((item) => {
      if (item.orderStatus === OrderItemStatus.Prepared) return item.preparedTime < (nextDocket as DocketFromDocket).docketGenerationTime;
      if (item.orderStatus === OrderItemStatus.Served) return item.completedTime < (nextDocket as DocketFromDocket).docketGenerationTime;
      return false;
    });
  }

  return items.every((item) => item.orderStatus === OrderItemStatus.Served && item.completedTime < (nextDocket as DocketFromDocket).docketGenerationTime);
};

const filterByProgress = (dockets: (OMSDisplayDocket & DocketFromDocket)[], operationMode: OperationMode, display: DisplayType, progress: ProgressType): (OMSDisplayDocket & DocketFromDocket)[] => {
  if (progress === ProgressType.All) return dockets;

  if (operationMode === OperationMode.OneStep) {
    if (progress === ProgressType.Active) return dockets.filter((d) => isDocketActive(d, operationMode, display));
    if (progress === ProgressType.Completed) return dockets.filter((d) => isDocketCompleted(d, operationMode, display));
  }

  const completionThreshold = display === DisplayType.PrepDisplay ? OrderItemStatus.Sent : OrderItemStatus.Prepared;

  if (progress === ProgressType.Active) {
    return dockets.filter((d) => (d.type === DocketType.NoteOnly ? d.noteOnlyStatus <= completionThreshold : d.docketItems.some((item) => item.orderStatus <= completionThreshold)));
  }

  if (progress === ProgressType.Completed) {
    return dockets.filter((d) => isDocketCompleted(d, operationMode, display));
  }

  return dockets;
};

const markFirstDocketItems = (docket: OMSDisplayDocket & DocketFromDocket): OMSDisplayDocket & DocketFromDocket => ({
  ...docket,
  docketItems: map(docket.docketItems, (item) => ({ ...item, isFromFirstDocket: true, docketItemGenerationTime: item.docketItemGenerationTime || docket.docketGenerationTime })),
});

const mergeDocketGroup = (group: (OMSDisplayDocket & DocketFromDocket)[], operationMode: OperationMode, display: DisplayType, mergeAllIntoOne: boolean): (OMSDisplayDocket & DocketFromDocket)[] => {
  const merged: (OMSDisplayDocket & DocketFromDocket)[] = [];

  for (const docket of group) {
    const isFirst = merged.length === 0;
    const previousDocket = merged[merged.length - 1];
    const shouldStartNewDocket = isFirst || (!mergeAllIntoOne && isDocketFinishedBeforeNext(previousDocket, docket, operationMode, display));

    if (shouldStartNewDocket) {
      merged.push(!mergeAllIntoOne && isFirst ? markFirstDocketItems(docket) : { ...docket, docketItems: map(docket.docketItems, (di) => ({ ...di, docketItemGenerationTime: di.docketItemGenerationTime || docket.docketGenerationTime })) });
    } else {
      const current = merged[merged.length - 1];
      merged[merged.length - 1] = {
        ...current,
        courseGroups: "courseGroups" in current && current.courseGroups?.length ? current.courseGroups : "courseGroups" in docket ? docket.courseGroups : [],
        docketItems: [...current.docketItems, ...map(docket.docketItems, (di) => ({ ...di, docketItemGenerationTime: di.docketItemGenerationTime || docket.docketGenerationTime }))],
      };
    }
  }

  return merged;
};

const mergeBookingDockets = (dockets: (OMSDisplayDocket & DocketFromDocket)[], operationMode: OperationMode, display: DisplayType, mergeAllIntoOne: boolean): (OMSDisplayDocket & DocketFromDocket)[] => {
  const result: (OMSDisplayDocket & DocketFromDocket)[] = [];
  const groups = groupDocketsByBookingId(dockets);

  for (const group of groups.values()) {
    result.push(...mergeDocketGroup(group, operationMode, display, mergeAllIntoOne));
  }

  return result;
};

const groupBookingDocketsByCompletion = (dockets: (OMSDisplayDocket & DocketFromDocket)[], operationMode: OperationMode, display: DisplayType, mergeAllIntoOne: boolean): (OMSDisplayDocket & DocketFromDocket)[] => {
  const { withBooking, withoutBooking } = splitByBooking(dockets);
  const sortedBookingDockets = orderBy(withBooking, (d) => d.docketGenerationTime);
  return [...withoutBooking, ...mergeBookingDockets(sortedBookingDockets, operationMode, display, mergeAllIntoOne)];
};

const computeDockets = (dockets: (OMSDisplayDocket & DocketFromDocket)[], operationMode: OperationMode, display: DisplayType, progress: ProgressType, hiddenKey: string, showHiddenDockets: boolean, singleDocketPerTable: boolean): OMSDisplayDocket[] => {
  const base = singleDocketPerTable ? groupBookingDocketsByCompletion(dockets, operationMode, display, false) : dockets;
  const filteredByHidden = base.filter((d) => (showHiddenDockets ? d.hidden[hiddenKey] : !d.hidden[hiddenKey]));
  let result = filterByProgress(filteredByHidden, operationMode, display, progress);
  if (singleDocketPerTable) {
    result = orderBy(groupBookingDocketsByCompletion(result, operationMode, display, true), (d) => d.docketGenerationTime);
  }

  // completed view shows newest dockets first
  if (progress === ProgressType.Completed) {
    result = orderBy(result, (d) => d.docketGenerationTime, "desc");
  }

  return result;
};

const getDocketGroupKey = (d: OMSDisplayDocket & DocketFromDocket, singleDocketPerTable: boolean) => {
  if (!singleDocketPerTable) return d._key;
  const bookingOrFunctionId = d.bookingId || d.functionBookingId;
  const _key = bookingOrFunctionId ? bookingOrFunctionId : d._key;
  return _key;
};

const computeAllPermutationCounts = (dockets: (OMSDisplayDocket & DocketFromDocket)[], orderMode: OrderModeType, hiddenKey: string, showHiddenDockets: boolean, singleDocketPerTable: boolean): Record<PermutationKey, ProgressCounts> => {
  const result = {} as Record<PermutationKey, ProgressCounts>;

  for (const operationMode of [OperationMode.AllStep, OperationMode.OneStep]) {
    for (const display of [DisplayType.PrepDisplay, DisplayType.ServeDisplay]) {
      if (orderMode === OrderModeType.SentOrder) {
        const base = singleDocketPerTable ? groupBookingDocketsByCompletion(dockets, operationMode, display, false) : dockets;
        const docketResult = base.filter((d) => (showHiddenDockets ? d.hidden[hiddenKey] : !d.hidden[hiddenKey]));
        result[toPermutationKey(operationMode, display)] = {
          allCount: size(
            groupBy(docketResult, (d) => {
              return getDocketGroupKey(d, singleDocketPerTable);
            })
          ),
          activeCount: size(
            groupBy(filterByProgress(docketResult, operationMode, display, ProgressType.Active), (d) => {
              return getDocketGroupKey(d, singleDocketPerTable);
            })
          ),
          completedCount: size(
            groupBy(filterByProgress(docketResult, operationMode, display, ProgressType.Completed), (d) => {
              return getDocketGroupKey(d, singleDocketPerTable);
            })
          ),
        };
      } else {
        result[toPermutationKey(operationMode, display)] = {
          allCount: dockets.length,
          activeCount: dockets.length,
          completedCount: dockets.filter((d) => isDocketCompleted(d, operationMode, display)).length,
        };
      }
    }
  }

  return result;
};

export const useFilteredDockets = (hiddenKey: string) => {
  const { display, linkedTable, operationMode, progress, orderMode, dockets, showHiddenDockets, singleDocketPerTable } = useAppSelector(
    (state) => ({
      display: state.root.display,
      linkedTable: state.root.linkedTable,
      operationMode: state.root.operationMode,
      progress: state.root.progress,
      orderMode: state.root.orderModeType,
      dockets: state.root.dockets,
      showHiddenDockets: state.settings.showHiddenDockets,
      singleDocketPerTable: state.settings.singleDocketPerTable,
    }),
    shallowEqual
  );

  const filteredDocketsByOrderMode = useMemo(() => {
    const baseDockets = dockets.filter((d) => (orderMode === OrderModeType.SentOrder ? d.docketGeneratedFrom === OMSDocketGeneratedFrom.Docket : d.docketGeneratedFrom === OMSDocketGeneratedFrom.Order)) as OMSDisplayDocket[];
    return (linkedTable ? baseDockets.filter((d) => linkedTable === d.tableNo) : baseDockets) as OMSDisplayDocket[];
  }, [dockets, linkedTable, orderMode]);

  const filteredDockets = useMemo(() => {
    if (orderMode !== OrderModeType.SentOrder) return filteredDocketsByOrderMode;
    return computeDockets(filteredDocketsByOrderMode as (OMSDisplayDocket & DocketFromDocket)[], operationMode, display, progress, hiddenKey, showHiddenDockets, singleDocketPerTable);
  }, [
    filteredDocketsByOrderMode, //
    operationMode,
    display,
    progress,
    orderMode,
    hiddenKey,
    showHiddenDockets,
    singleDocketPerTable,
  ]);

  const allPermutationCounts = useMemo(() => {
    return computeAllPermutationCounts(filteredDocketsByOrderMode as (OMSDisplayDocket & DocketFromDocket)[], orderMode, hiddenKey, showHiddenDockets, singleDocketPerTable);
  }, [filteredDocketsByOrderMode, orderMode, hiddenKey, showHiddenDockets, singleDocketPerTable]);

  const { allCount, activeCount, completedCount } = allPermutationCounts[toPermutationKey(operationMode, display)];
  return {
    filteredDockets,
    allCount,
    activeDockets: activeCount,
    completedDocketNumber: completedCount,
  };
};
